import React from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { useChatHistory } from "../../contexts/ChatHistoryContext";

const formatTimeAgo = (timestamp) => {
  try {
    const past = new Date(timestamp);
    if (Number.isNaN(past.getTime())) return "Recently";

    const diffMs = Date.now() - past.getTime();
    if (diffMs < 0) return "Just now";

    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);

    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 30) return `${days}d ago`;
    return "Long ago";
  } catch (error) {
    return "Recently";
  }
};

/**
 * The conversation rail.
 *
 * Same job as before — a list of questions you have already asked — but the
 * rows are real buttons now. They used to be `div`s with an onClick, which
 * meant they could not be reached or activated from the keyboard at all.
 */
const ChatHistory = ({ onQuestionSelect, onNewChat }) => {
  const { chatHistory, clearHistory } = useChatHistory();

  return (
    <div className="flex h-full flex-col bg-iq-rail">
      <div className="shrink-0 p-3">
        {onNewChat && (
          <button
            type="button"
            onClick={onNewChat}
            className="flex w-full items-center gap-2 rounded-xl border border-iq-line bg-white px-3 py-2.5 text-[13.5px] font-medium text-iq-ink shadow-sm transition-colors hover:border-iq-brand/40 hover:bg-iq-brand-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
          >
            <FiPlus className="h-4 w-4 text-iq-brand" />
            New chat
          </button>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between px-4 pb-1.5 pt-1">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.07em] text-iq-ink-faint">
          Recent questions
        </h2>
        {chatHistory.length > 0 && (
          <button
            type="button"
            onClick={clearHistory}
            aria-label="Clear recent questions"
            className="rounded-md p-1 text-iq-ink-faint transition-colors hover:bg-white hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
          >
            <FiTrash2 size={13} />
          </button>
        )}
      </div>

      <div className="iq-scroll min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {chatHistory.length === 0 ? (
          <p className="px-2 py-2 text-[12.5px] leading-relaxed text-iq-ink-faint">
            Questions you ask will collect here so you can pick one up again.
          </p>
        ) : (
          <ul className="space-y-0.5">
            {chatHistory.map((entry, index) => (
              <li key={`${entry.timestamp}-${index}`}>
                <button
                  type="button"
                  // threadId is passed so the conversation can be RESUMED
                  // rather than re-asked from scratch. Entries loaded from the
                  // server always carry one; an optimistic entry added in this
                  // tab may not, and falls back to asking fresh.
                  onClick={() => onQuestionSelect?.(entry.text, entry.threadId)}
                  title={entry.text}
                  className="w-full rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
                >
                  <span className="block truncate text-[13px] font-medium text-iq-ink">
                    {entry.text}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-iq-ink-faint">
                    {formatTimeAgo(entry.timestamp)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ChatHistory;
